import { z } from "zod";

/**
 * The partner dashboard: what an organiser sees of her own trips, guests and
 * money. Every amount is MAD, whole.
 */

/** Hours a guest has to send the deposit before her booking reads as overdue. */
export const DEPOSIT_GRACE_HOURS = 48;

export type PartnerDepositState = "to_check" | "overdue" | "received" | "none";

/**
 * Where a booking stands with its deposit, for the organiser:
 * `received` once marked, `overdue` when still pending 48 h after booking,
 * `to_check` while pending inside that window, `none` once the booking is
 * confirmed without a mark, cancelled, or the trip asks no deposit.
 */
export function partnerDepositState(input: {
  status: string;
  depositAmount: number | null | undefined;
  depositReceivedAt: string | Date | null | undefined;
  createdAt: string | Date;
  now?: Date;
}): PartnerDepositState {
  if (!input.depositAmount || input.depositAmount <= 0) return "none";
  if (input.status === "cancelled") return "none";
  if (input.depositReceivedAt) return "received";
  if (input.status !== "pending") return "none";
  const created = new Date(input.createdAt).getTime();
  const now = (input.now ?? new Date()).getTime();
  return now - created > DEPOSIT_GRACE_HOURS * 3600_000 ? "overdue" : "to_check";
}

export interface PartnerOrganiserDTO {
  id: string;
  slug: string;
  name: string;
}

export interface PartnerMeDTO {
  organisers: PartnerOrganiserDTO[];
}

export interface PartnerTripDateDTO {
  date: string;
  seatsSold: number;
  /** Null when the trip has no seat limit. */
  seatsLeft: number | null;
  toCheck: number;
  overdue: number;
}

export interface PartnerTripDTO {
  activityId: string;
  slug: string;
  title: string;
  seatLimit: number | null;
  depositAmount: number | null;
  departures: string[];
  /**
   * Every date set on the trip, past ones included, sorted. The settings
   * screen edits this list and sends it back whole.
   */
  tripDates: string[];
  /** Upcoming dates first, then up to 60 days of past ones. */
  dates: PartnerTripDateDTO[];
}

export interface PartnerOverviewDTO {
  organiser: PartnerOrganiserDTO;
  toCheck: number;
  overdue: number;
  /** Seats sold on upcoming dates, cancelled bookings excluded. */
  seatsSold: number;
  trips: PartnerTripDTO[];
}

export interface PartnerGuestDTO {
  reservationId: string;
  reference: string;
  status: "pending" | "confirmed" | "completed" | "paid" | "cancelled";
  name: string;
  email: string | null;
  phone: string | null;
  adults: number;
  children: number;
  infants: number;
  seats: number;
  departure: string | null;
  total: number;
  deposit: number;
  rest: number;
  depositState: PartnerDepositState;
  depositReceivedAt: string | null;
  createdAt: string;
  notes: string | null;
}

export interface PartnerDateDetailDTO {
  activityId: string;
  title: string;
  date: string;
  seatLimit: number | null;
  seatsSold: number;
  seatsLeft: number | null;
  departures: string[];
  guests: PartnerGuestDTO[];
}

export interface PartnerMoneyTripDTO {
  activityId: string;
  title: string;
  date: string;
  guests: number;
  commission: number;
}

export interface PartnerMoneyDTO {
  /** "YYYY-MM". */
  month: string;
  /** Adults + children on completed bookings dated in the month. */
  guests: number;
  /** What the organiser owes MaJourneys for the month, 0 while terms are unset. */
  commission: number;
  /** False while no trip of hers carries a commission yet. */
  termsSet: boolean;
  byTrip: PartnerMoneyTripDTO[];
}

const ISO_DATE = /^\d{4}-\d{2}-\d{2}$/;

export const partnerTripPatchSchema = z.object({
  seatLimit: z.number().int().min(1).max(500).nullable().optional(),
  depositAmount: z.number().int().min(0).max(100000).nullable().optional(),
  tripDates: z.array(z.string().regex(ISO_DATE)).max(200).optional(),
  tripDepartures: z.array(z.string().trim().min(1).max(120)).max(10).optional(),
});
export type PartnerTripPatch = z.infer<typeof partnerTripPatchSchema>;

export const partnerCancelSchema = z.object({
  reason: z.string().trim().max(500).optional(),
});

export const organiserMembersSchema = z.object({
  emails: z.array(z.string().trim().toLowerCase().email()).max(20),
});
